import { Helmet } from "react-helmet-async";
import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";

import BreakingNews from "../../components/BreakingNews";
import { getMediaBaseUrl } from "../../utils/backendUrl";
import RightSidebar from "../../components/RightSidebar";
import NewsCard from "../../components/NewsCard";
import Profilecard from "../../components/Profilecard";

import TopAdBanner from "../../components/TopAdBanner";
import LeftAdBanner from "../../components/LeftAdBanner";
import WeatherWidget from "../../components/WeatherWidget";
import ViewAllHeader from "../../common/ViewAllHeader";
import { dummyNewsData } from "../../utils/dummyNewsData";
import { videonews } from "../home/homeData.js";
import { getNewsByFilter } from "../../api/apiHandler";

const NewsDetail = () => {
  const { id } = useParams();

  const [news, setNews] = useState(null);
  const [moreNews, setMoreNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blogNews, setBlogNews] = useState([]);

  const NEWS_BASE = getMediaBaseUrl();

  useEffect(() => {
    let isMounted = true;
    getNewsByFilter({ limit: 10 })
      .then((res) => {
        if (!isMounted) return;
        const items = Array.isArray(res?.data) ? res.data : [];
        const mapped = items.map((item) => ({
          id: item.id || item._id,
          image: item.thumbnail
            ? item.thumbnail.startsWith("http")
              ? item.thumbnail
              : item.thumbnail.startsWith("/images/")
                ? item.thumbnail
                : `${NEWS_BASE}/uploads/news/${encodeURIComponent(item.thumbnail)}`
            : "",
          category: item.category || "",
          title: item.news_title || item.title || "",
          author: item.reporter_name || "न्यूज़ डेस्क",
          date: item.createdAt ? new Date(item.createdAt).toLocaleDateString("hi-IN") : "",
          readTime: "2 मिनट पढ़ें",
          fullNews: item.description || item.fullNews || "",
          videoUrl: item.youtube_url || "",
        }));
        setBlogNews(mapped.slice(0, 3));
      })
      .catch((err) => console.error("Error fetching latest news:", err));
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    // 🔥 Scroll to top
    window.scrollTo({ top: 0, behavior: "auto" });

    // 🔥 VERY IMPORTANT: Stop any running speech before loading new page
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }

    setLoading(true);

    // If it's a dummy article, find it from the local data directly
    if (id && id.startsWith("dummy-")) {
      const dummyArticle = dummyNewsData.find(item => item.id === id);
      setNews(dummyArticle || null);
      if (dummyArticle) {
        const relatedDummy = dummyNewsData.filter(
          item => item.id !== id && item.category === dummyArticle.category
        );
        setMoreNews(relatedDummy.slice(0, 6));
      }
      setLoading(false);
      return;
    }

    // ================= FETCH NEWS & FILTER RELATED =================
    Promise.all([
      axios.get(`${process.env.REACT_APP_BACKEND_URL}product/one/${id}`).catch(() => null),
      axios.get(`${process.env.REACT_APP_BACKEND_URL}product/related/${id}`).catch(() => null),
      axios.get(`${process.env.REACT_APP_BACKEND_URL}product`).catch(() => ({ data: [] }))
    ]).then(([mainRes, relatedRes, moreRes]) => {

      const mainArticle = mainRes?.data;

      if (mainArticle) {
        setNews(mainArticle);
        let related = relatedRes?.data || [];
        if (related.length === 0) {
          const allNews = moreRes?.data || [];
          related = allNews.filter(
            item => item.id !== mainArticle.id &&
              (item.category || "").toLowerCase() === (mainArticle.category || "").toLowerCase()
          );
        }
        setMoreNews(related.slice(0, 6));
      } else {
        // Fallback to dummy data if API fails
        const dummyArticle = dummyNewsData[0];
        setNews(dummyArticle);
        setMoreNews(dummyNewsData.filter(item => item.id !== dummyArticle.id && item.category === dummyArticle.category).slice(0, 6));
      }
      setLoading(false);

    }).catch((err) => {
      console.error("Error fetching data:", err);
      const dummyArticle = dummyNewsData[0];
      setNews(dummyArticle);
      setMoreNews(dummyNewsData.filter(item => item.id !== dummyArticle.id && item.category === dummyArticle.category).slice(0, 6));
      setLoading(false);
    });

    // 🔥 Cleanup on unmount
    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
      </div>
    );
  }

  if (!news) {
    return (
      <div className="p-20 text-center bg-gray-50 min-h-screen">
        <h2 className="text-2xl font-bold text-gray-700">उफ़! खबर नहीं मिली।</h2>
      </div>
    );
  }

  const BASE_URL = getMediaBaseUrl();

  const getImageUrl = (thumbnail, id, cat) => {
    if (!thumbnail) return "/logo.png";
    if (thumbnail?.startsWith("http")) {
      return thumbnail;
    } else if (thumbnail?.startsWith("/images/")) {
      return thumbnail; // Fallback for local dummy images if any remain
    } else {
      return `${BASE_URL}/uploads/news/${encodeURIComponent(thumbnail)}`;
    }
  };

  const toAbsoluteUrl = (url) => {
    if (!url) return `${window.location.origin}/logo.png`;
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith("/")) return `${window.location.origin}${url}`;
    return `${window.location.origin}/${url}`;
  };

  let thumbnailUrl = getImageUrl(news.thumbnail, news.id, news.category || news.state);

  // Strip HTML tags from description for OG / Twitter meta
  const plainDescription = news.description
    ? news.description.replace(/<[^>]*>/g, '').trim().slice(0, 160)
    : 'मध्य प्रदेश और छत्तीसगढ़ की ताज़ा खबरें हिंदी और बुंदेली में।';

  const pageUrl = `${window.location.origin}/news/${news.id}`;
  const ogImageUrl = toAbsoluteUrl(thumbnailUrl);

  return (
    <div className="bg-gray-50 min-h-screen pb-12">
      {/* ================= SEO ================= */}
      <Helmet>
        <title>{news.news_title} | The Mandakini Times</title>

        <meta name="description" content={plainDescription} />

        <meta property="og:type"              content="article" />
        <meta property="og:site_name"         content="The Mandakini Times" />
        <meta property="og:title"             content={news.news_title} />
        <meta property="og:description"       content={plainDescription} />
        <meta property="og:url"               content={pageUrl} />
        <meta property="og:image"             content={ogImageUrl} />
        <meta property="og:image:secure_url"  content={ogImageUrl} />
        <meta property="og:image:width"       content="1200" />
        <meta property="og:image:height"      content="630" />
        <meta property="og:image:alt"         content={news.news_title} />

        {/* Twitter / X Card */}
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:title"       content={news.news_title} />
        <meta name="twitter:description" content={plainDescription} />
        <meta name="twitter:image"       content={ogImageUrl} />
      </Helmet>

      <div className="max-w-[1400px] mx-auto px-4 pt-6">
        <BreakingNews />

        <TopAdBanner />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-4">
          {/* ================= LEFT SIDEBAR ================= */}
          <div className="hidden lg:block lg:col-span-2">
            <div className="space-y-3 sticky top-[70px]">
              <LeftAdBanner />
              <div>
                <WeatherWidget defaultCity="खजुराहो" />
              </div>
            </div>
          </div>

          {/* ================= MAIN ARTICLE & EXTRAS ================= */}
          <div className="lg:col-span-8">
            <Profilecard data={news} isDetail={true} />



        {/* ================= MORE NEWS ================= */}
        <section className="mt-8 bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-8 border-b-2 border-gray-100 pb-3">
            <div className="w-2 h-8 bg-purple-700 rounded-full"></div>
            <h2 className="text-2xl font-black text-[#4600a3] tracking-wide">
              और खबरें
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {moreNews.map((item) => (
              <NewsCard
                key={item.id}
                title={item.news_title}
                image={getImageUrl(item.thumbnail, item.id, item.category || item.state)}
                category={item.category || item.state || "News"}
                date={item.createdAt}
                fullNews={item.description}
                author={item.reporter_name || "The Mandakini Times"}
                readTime="2 Min Read"
                link={`/news/${item.id}`}
                videoUrl={item.youtube_url}
              />
            ))}
          </div>
        </section>

        {/* 🔹 TOI-STYLE VIDEO HUB */}
        <section className="my-6 md:my-10 py-8 md:py-12 bg-rich-black -mx-4 px-4 lg:-mx-8 lg:px-8 rounded-none">
          <div className="flex justify-between items-end mb-6">
            <h2 className="text-2xl md:text-3xl font-black text-white uppercase tracking-wide flex items-center gap-3">
              <i className="fa-solid fa-circle-play text-brand-red"></i> वीडियो हब
            </h2>
            <Link to="/gallery/videos" className="text-gray-400 hover:text-white text-sm font-bold transition">सभी देखें &rarr;</Link>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8">
              <div className="bg-black rounded-lg overflow-hidden border border-zinc-800 relative">
                <div className="absolute top-4 left-4 bg-brand-red text-white px-3 py-1 text-[11px] font-black rounded shadow-lg animate-pulse z-10 flex items-center gap-2 uppercase tracking-widest">
                  <div className="w-2 h-2 bg-white rounded-full"></div> लाइव
                </div>
                <div className="w-full aspect-video relative">
                  <iframe 
                    className="absolute inset-0 w-full h-full"
                    src="https://www.youtube.com/embed/L7fIF53G_P4?autoplay=0" 
                    title="YouTube Live Stream" 
                    frameBorder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                    allowFullScreen
                  ></iframe>
                </div>
                <div className="p-4 bg-zinc-900 border-t border-zinc-800">
                  <h3 className="text-white text-lg md:text-xl font-bold">LIVE: देश-दुनिया की तमाम बड़ी खबरें, देखें सीधा प्रसारण</h3>
                  <p className="text-gray-400 text-sm mt-2">मंदाकिनी टाइम्स न्यूज़ डेस्क • 12K देख रहे हैं</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-4 bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden flex flex-col">
              <div className="bg-zinc-800 text-white font-bold px-4 py-3 border-b border-zinc-700 text-sm uppercase tracking-wide">
                अप नेक्स्ट
              </div>
              <div className="flex-1 overflow-y-auto max-h-[350px] lg:max-h-[400px] p-2 space-y-2 scrollbar-thin scrollbar-thumb-zinc-700">
                {videonews.map((item, i) => (
                  <div key={i} className="flex gap-3 p-2 hover:bg-zinc-800 rounded-md cursor-pointer transition group">
                    <div className="relative w-28 shrink-0 aspect-video rounded overflow-hidden">
                      <img src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-300" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <i className="fa-solid fa-play text-white opacity-80 group-hover:opacity-100 group-hover:text-brand-red transition"></i>
                      </div>
                    </div>
                    <div className="flex flex-col justify-center">
                      <h4 className="text-gray-200 text-[13px] font-bold line-clamp-3 group-hover:text-brand-red transition leading-snug">
                        {item.title}
                      </h4>
                      <span className="text-zinc-500 text-[10px] mt-1 uppercase font-bold">{item.readTime || "2:45"}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* BLOGS SECTION */}
        <section className="my-8 pt-6 border-t border-gray-200">
          <ViewAllHeader title="ब्लॉग्स" to="/blog" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogNews.map((item, i) => (
              <NewsCard key={item.id || `blog-${i}`} {...item} hideListenButton={true} />
            ))}
          </div>
        </section>

          </div>

          {/* ================= RIGHT SIDEBAR ================= */}
          <div className="lg:col-span-2">
            <RightSidebar />
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsDetail;
